import {
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, CheckCircle, ExternalLink, FileSearch, Loader2, Package, ShoppingCart, Users, Plus, XCircle, ArrowLeft } from 'lucide-react';
import { useGetList, useGetOne, useRecordContext } from 'ra-core';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { StatusBadge } from '../documents/shared/StatusBadge';
import type { BoM, RFQ } from '../types';
import { SendRFQButton } from './SendRFQButton';
import { VendorQuoteForm } from './VendorQuoteForm';
import { FinalizeQuoteDialog } from './FinalizeQuoteDialog';

const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/rfqs" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4" /> Back to RFQs
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight text-slate-900 ml-1">{record.reference}</span>
        </div>
    );
};

const RFQShowActions = () => (
    <div className="flex items-center gap-2">
        <SendRFQButton />
        <EditButton />
    </div>
);

const RFQShowContent = () => {
    const record = useRecordContext<RFQ>();
    const navigate = useNavigate();
    const [isAddQuoteOpen, setIsAddQuoteOpen] = useState(false);
    const [finalizeQuoteOpen, setFinalizeQuoteOpen] = useState(false);
    const [selectedFinalizeQuote, setSelectedFinalizeQuote] = useState<any>(null);
    
    // Fetch the linked BoM to display its items
    const { data: bom, isPending: bomLoading } = useGetOne<BoM>(
        'boms',
        { id: record?.bom_id },
        { enabled: !!record?.bom_id }
    );

    // Fetch vendor quotes from the database
    const { data: vendorQuotesData, isPending: quotesLoading } = useGetList('vendor_quotes', {
        pagination: { page: 1, perPage: 100 },
        filter: { rfq_id: record?.id }
    }, { enabled: !!record?.id });

    if (!record) return null;

    const vendorQuotes = vendorQuotesData || [];
    const lowestQuote = vendorQuotes.length > 0 
        ? vendorQuotes.reduce((min: any, q: any) => (q.total_amount || 0) < (min.total_amount || 0) ? q : min, vendorQuotes[0])
        : null;

    const handleGeneratePO = (quote: any) => {
        navigate('/purchase_orders/create', {
            state: {
                record: {
                    vendor_id:  quote.vendor_id,
                    project_id: record.project_id,
                    rfq_id:     record.id,
                    status:     'draft',
                    // BOM items now have description directly. BOM-2: never fall
                    // back to the raw inventory_item_id (it printed a bare numeric
                    // ID instead of the material name on the generated PO).
                    items: quote.items || bom?.items?.map((item: any) => ({
                        description: item.description || '',
                        hsn_code:    item.hsn_code || '',
                        quantity:    item.quantity,
                        unit:        item.unit,
                        unit_price:  quote.unit_price || 0,
                    })) || [],
                },
            },
        });
    };

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8">
            <div className="flex items-center gap-4 text-sm text-slate-500 border-b pb-4 flex-wrap">
                <StatusBadge status={record.status} />
                <span className="text-slate-300">|</span>
                <div className="flex items-center gap-1 font-medium">
                    <span className="text-slate-400">Project:</span>
                    <ReferenceField source="project_id" reference="projects" link="show">
                        <TextField source="name" className="text-blue-600 hover:underline" />
                    </ReferenceField>
                </div>
                <span className="text-slate-300">|</span>
                <div className="flex items-center gap-1 font-medium">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400">Due:</span>
                    <DateField source="due_date" />
                </div>
            </div>

            <Tabs defaultValue="vendors" className="w-full">
                <TabsList className="grid w-full max-w-lg grid-cols-3 mb-8">
                    <TabsTrigger value="vendors">Target Vendors</TabsTrigger>
                    <TabsTrigger value="items">Requested Items</TabsTrigger>
                    <TabsTrigger value="quotes">Quote Comparison</TabsTrigger>
                </TabsList>

                <TabsContent value="vendors">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                <Users className="w-4 h-4" /> Invited Vendors
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {record.vendor_ids?.map((vendorId) => (
                                    <div key={vendorId} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100">
                                        <ReferenceField record={{ id: vendorId, vendor_id: vendorId }} source="vendor_id" reference="vendors" link="show">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-white rounded flex items-center justify-center border text-xs font-bold text-slate-400">
                                                    V
                                                </div>
                                                <TextField source="name" className="font-bold text-slate-900" />
                                            </div>
                                        </ReferenceField>
                                        <Badge variant="secondary" className="text-[10px] uppercase font-bold">Pending Quote</Badge>
                                    </div>
                                ))}
                                {(!record.vendor_ids || record.vendor_ids.length === 0) && (
                                    <div className="col-span-2 py-8 text-center text-slate-400 italic text-sm">No vendors selected for this RFQ.</div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="items">
                     <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                <Package className="w-4 h-4" /> Items from BoM
                            </CardTitle>
                            <ReferenceField source="bom_id" reference="boms" link="show">
                                <div className="flex items-center gap-1 text-xs text-blue-600 font-bold hover:underline cursor-pointer">
                                    <ExternalLink className="w-3 h-3" /> View Source BoM
                                </div>
                            </ReferenceField>
                        </CardHeader>
                        <CardContent>
                            {bomLoading ? (
                                <div className="py-12 text-center text-slate-400">
                                    <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-slate-200" />
                                    <p className="text-sm">Fetching items from BoM...</p>
                                </div>
                            ) : bom?.items && bom.items.length > 0 ? (
                                <div className="rounded-md border overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-slate-50/50">
                                                <TableHead>Material / Component</TableHead>
                                                <TableHead className="text-right">Qty</TableHead>
                                                <TableHead>Unit</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {bom.items.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell className="font-semibold text-slate-900">
                                                        {(item as any).description || '—'}
                                                    </TableCell>
                                                    <TableCell className="text-right font-bold">{item.quantity}</TableCell>
                                                    <TableCell>{item.unit}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="py-12 text-center text-slate-400">
                                    <FileSearch className="w-12 h-12 mx-auto mb-4 text-slate-200" />
                                    <p className="text-sm italic">No items found in the linked Bill of Materials.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="quotes">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                <ShoppingCart className="w-4 h-4" /> Vendor Quote Comparison
                            </CardTitle>
                            <Button size="sm" onClick={() => setIsAddQuoteOpen(true)} className="flex items-center gap-2">
                                <Plus className="w-4 h-4" /> Add Vendor Quote
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {vendorQuotes.length > 0 ? (
                                <div className="space-y-4">
                                    <div className="rounded-md border overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-slate-50/50">
                                                    <TableHead>Vendor</TableHead>
                                                    <TableHead className="text-right">Base Price</TableHead>
                                                    <TableHead className="text-right">Logistics</TableHead>
                                                    <TableHead className="text-right">Unit Price</TableHead>
                                                    <TableHead className="text-right">Total Amount</TableHead>
                                                    <TableHead>Delivery Terms</TableHead>
                                                    <TableHead>Lead Time</TableHead>
                                                    <TableHead className="text-center">Status</TableHead>
                                                    <TableHead />
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {vendorQuotes.map((quote: any, idx: number) => {
                                                    const isLowest = lowestQuote && quote.vendor_id === lowestQuote.vendor_id;
                                                    return (
                                                        <TableRow key={idx} className={isLowest ? 'bg-emerald-50/30' : ''}>
                                                            <TableCell>
                                                                <ReferenceField record={{ id: quote.vendor_id, vendor_id: quote.vendor_id }} source="vendor_id" reference="vendors" link="show">
                                                                    <TextField source="name" className="font-bold text-slate-900" />
                                                                </ReferenceField>
                                                            </TableCell>
                                                            <TableCell className="text-right font-mono">
                                                                ₹{(quote.base_price || 0).toLocaleString('en-IN')}
                                                            </TableCell>
                                                            <TableCell className="text-right font-mono">
                                                                ₹{(quote.logistics_cost || 0).toLocaleString('en-IN')}
                                                            </TableCell>
                                                            <TableCell className="text-right font-mono">
                                                                ₹{(quote.unit_price || quote.base_price || 0).toLocaleString('en-IN')}
                                                            </TableCell>
                                                            <TableCell className="text-right font-bold text-slate-900">
                                                                ₹{(quote.total_amount || quote.base_price || 0).toLocaleString('en-IN')}
                                                            </TableCell>
                                                            <TableCell className="text-sm">{quote.delivery_terms || '—'}</TableCell>
                                                            <TableCell className="text-sm">{quote.lead_time || '—'}</TableCell>
                                                            <TableCell className="text-center">
                                                                {quote.status === 'approved' ? (
                                                                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px] font-bold">
                                                                        <CheckCircle className="w-3 h-3 mr-1" /> Approved
                                                                    </Badge>
                                                                ) : quote.status === 'rejected' ? (
                                                                    <Badge className="bg-red-50 text-red-600 border-red-100 text-[9px] font-bold">
                                                                        <XCircle className="w-3 h-3 mr-1" /> Rejected
                                                                    </Badge>
                                                                ) : isLowest ? (
                                                                    <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-[9px] font-bold">
                                                                         Lowest Valid
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge variant="outline" className="text-[9px]">Quoted</Badge>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <div className="flex justify-end gap-2">
                                                                    {/* Finalize Button - Only if RFQ not finalized and quote not approved/rejected */}
                                                                    {record.status !== 'finalized' && quote.status !== 'rejected' && quote.status !== 'approved' && (
                                                                        <Button
                                                                            variant="default"
                                                                            size="sm"
                                                                            className="text-[10px] h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                                                                            onClick={() => {
                                                                                setSelectedFinalizeQuote(quote);
                                                                                setFinalizeQuoteOpen(true);
                                                                            }}
                                                                        >
                                                                            Finalize
                                                                        </Button>
                                                                    )}
                                                                    
                                                                    {/* Generate PO - Only for Approved quotes now, or if it was the old behavior, we'll keep it for approved */}
                                                                    {(quote.status === 'approved' || (record.status !== 'finalized' && quote.status !== 'rejected')) && (
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="text-[10px] h-7 border-slate-200 text-slate-600"
                                                                            onClick={() => handleGeneratePO(quote)}
                                                                            title={quote.status === 'approved' ? "Generate Final PO" : "Generate Draft PO"}
                                                                        >
                                                                            Create PO
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            ) : quotesLoading ? (
                                <div className="py-12 text-center text-slate-400">
                                    <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-slate-200" />
                                    <p className="text-sm">Loading vendor quotes...</p>
                                </div>
                            ) : (
                                <div className="py-12 text-center text-slate-400">
                                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-slate-200" />
                                    <h3 className="font-bold text-slate-900 mb-1">No Quotes Received Yet</h3>
                                    <p className="text-sm max-w-sm mx-auto">
                                        Vendor quotes will appear here once vendors respond to this RFQ. 
                                        Use the "Send RFQ" button to invite vendors.
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
            <VendorQuoteForm 
                rfqId={record.id} 
                open={isAddQuoteOpen} 
                onOpenChange={setIsAddQuoteOpen}
                onSuccess={() => {}}
            />

            {selectedFinalizeQuote && (
                <FinalizeQuoteDialog
                    open={finalizeQuoteOpen}
                    onOpenChange={setFinalizeQuoteOpen}
                    rfqId={record.id}
                    quoteId={selectedFinalizeQuote.id}
                    vendorName={`Vendor ID: ${selectedFinalizeQuote.vendor_id}`}
                />
            )}
        </div>
    );
};

export const RFQShow = () => (
    <Show title={<ShowTitle />} actions={<RFQShowActions />}>
        <RFQShowContent />
    </Show>
);
